// Extensions a reader treats as one format. Listing either one accepts both, so a
// `.jpg` restriction does not reject the same photo saved as `.jpeg`.
const extensionEquivalents: Array<ReadonlySet<string>> = [new Set(['jpg', 'jpeg'])];

export const normalizeExtension = (extension: string): string => {
  const cleaned = extension.trim();
  return (cleaned.startsWith('.') ? cleaned.slice(1) : cleaned).toLowerCase();
};

export const equivalentExtensions = (extension: string): Array<string> => {
  const group = extensionEquivalents.find((equivalents) => equivalents.has(extension));
  return group ? [...group] : [extension];
};

/**
 * Every extension an `accept` list allows, equivalents expanded. An entry that
 * normalizes to nothing is dropped, so an accept list with no usable extension
 * yields none and the field restricts nothing — `allowed_extensions_for_field`
 * in `apps/forms/utils.py` makes the same call.
 */
export const parseAcceptExtensions = (accept: string | undefined): Array<string> => {
  if (typeof accept !== 'string' || !accept) return [];
  return accept.split(',').flatMap((entry) => {
    const extension = normalizeExtension(entry);
    return extension ? equivalentExtensions(extension) : [];
  });
};

/**
 * The extension of a file's own name — deliberately not `getFileExtension`, which
 * parses its input as a URL first and so reads `note:2024.pdf#x.exe` as `pdf` where
 * the server, splitting the name it actually stores, reads `exe`.
 */
const fileNameExtension = (fileName: string): string =>
  fileName.includes('.') ? normalizeExtension(fileName.split('.').pop() ?? '') : '';

export const isFileNameAllowed = (fileName: string, allowedExtensions: Array<string>): boolean => {
  const extension = fileNameExtension(fileName);
  return extension ? allowedExtensions.includes(extension) : false;
};
